/**
 * v4 sign-in / email-capture — THE account state, and every door derived from it.
 *
 * Spec: "Sign in — user journeys" (Google Doc, 2026-08-17), rule 4:
 *
 *   > One account state, and every door renders from it — the homepage
 *   > top-right, the sticky footer's sign-in, the You card and its dot. […]
 *   > Not tidiness: while building we found three separate places that each
 *   > decided this for themselves, and each had to be found by hand.
 *
 * So this module is pure and prop-driven on purpose: no Firebase, no hooks, no
 * storage. It is the single place that answers "given who she is, what does
 * each door show?", and it is unit-testable without mounting anything. The
 * production hooks (`useFriendsList`, `onAuthStateChanged`, `useEmailCaptured`)
 * feed it; they do not each re-decide.
 *
 * Every selector returns a DISCRIMINATED UNION rather than a boolean pile, so a
 * new state without a rendering is a compile error rather than a blank slot —
 * the same discipline `SignInModal`'s `INTENT_HEADING` uses.
 *
 * THREE INPUTS decide everything (spec, "How to read this"): which browser she
 * is in, whether she has a board, and whether she has given us her email.
 * Note "has a board", NOT "took the quiz" — the two come apart (she signs in on
 * her laptop having quizzed on her phone; or Instagram wipes the board she just
 * made).
 */

import type { CaptureStatus } from './captureRecord';

export type { CaptureStatus };

/** Instagram's in-app browser behaves differently enough to be its own axis. */
export type BrowserKind = 'normal' | 'webview';

/** One board, as every aggregate surface needs to name it. */
export interface BoardSummary {
  id: string;
  /** The recipient's DISPLAY name ("Mom"), never the relationship enum. */
  name: string;
  /** Resolved emoji. Resolution is FE-only (architecture.md) — pass it in. */
  emoji: string;
  /**
   * The recipient doc's own `isMe` (her own board, not someone else's).
   * Optional — the fixtures/claim-preview callers that build a `BoardSummary`
   * by hand have no occasion to know this, and only the tap sign-in pop-up's
   * own-board headline variants (THE-975) read it.
   */
  isMe?: boolean;
}

export interface AccountState {
  signedIn: boolean;
  /** NEWEST FIRST. Length is the "has a board" axis; the list is what the
   *  1-vs-2+ naming rule reads. */
  boards: BoardSummary[];
  /** The address she gave us, or null if she never has. Stored in full and
   *  shown in full — it is the only place a typo ever becomes visible. */
  capturedEmail: string | null;
  /**
   * Whether that address's WRITE was ever observed to succeed. Three-state, not
   * a boolean, because "she typed one and we never saw the response" is the
   * common case, not an edge — see `captureRecord.ts`. Only `confirmed` may
   * produce copy that claims we emailed her, and only `confirmed` silences the
   * ask.
   */
  captureStatus: CaptureStatus;
  /**
   * The SIGNED-IN account's own identity — read for the avatar's initials and
   * the account dropdown header. Null/absent for anon, because there is no
   * identity to show: the avatar falls back to a person icon and the dropdown
   * does not render. Deliberately NOT `capturedEmail`, which is the PRE-sign-in
   * capture address a signed-in user may never have given.
   */
  displayName?: string | null;
  accountEmail?: string | null;
  browser: BrowserKind;
  /** Backend-materialised alerts. Surfaced ONLY when signed in — see
   *  `alertsVisible`. Signed-out gating is front-end only; the nightly job
   *  keeps generating them. */
  alertCount: number;
}

export const EMPTY_ACCOUNT_STATE: AccountState = {
  signedIn: false,
  boards: [],
  capturedEmail: null,
  captureStatus: 'none',
  displayName: null,
  accountEmail: null,
  browser: 'normal',
  alertCount: 0,
};

export function hasBoard(s: AccountState): boolean {
  return s.boards.length > 0;
}

/** She has given us an address at all — confirmed or not. */
export function hasAttemptedCapture(s: AccountState): boolean {
  return s.captureStatus !== 'none' && Boolean(s.capturedEmail);
}

/**
 * The write landed. THE ONLY predicate that may silence the ask or licence copy
 * that says we emailed her. Deliberately not "she typed an address".
 */
export function isCaptureConfirmed(s: AccountState): boolean {
  return s.captureStatus === 'confirmed' && Boolean(s.capturedEmail);
}

/**
 * THE shared navigation predicate.
 *
 * Spec to-do 8: "Ungate navigation from having a board: the homepage layout and
 * the tab bar both become 'signed in OR has a board', behind one shared rule so
 * they cannot drift apart."
 *
 * Three implementations exist on master and only one is already correct:
 *   - `MobileTabBarMount` — already `isPermanent || friends.length > 0`. ✅
 *   - `LandingPage`'s `showSignedInLayout` — board count only, so signing in
 *     with nothing saved strands her on the marketing page (spec journey 6).
 *   - `useDesktopNav` — board count only, same bug.
 *
 * Both of the latter should call THIS. Doing that is a production change and is
 * therefore out of scope for the preview route; the predicate ships first so
 * the rewire is a one-line import rather than a fourth opinion.
 */
export function navUnlocked(s: AccountState): boolean {
  return s.signedIn || hasBoard(s);
}

/**
 * Signed-out visitors never see birthday or event alerts. Front-end gate only —
 * the backend keeps materialising them (spec, rule 3).
 */
export function alertsVisible(s: AccountState): boolean {
  return s.signedIn;
}

/* ── The You-tab badge ────────────────────────────────────────────────── */

export type YouBadge =
  | { kind: 'none' }
  /** "Save your boards / sign in". A DOT, because there is nothing to count. */
  | { kind: 'dot' }
  /** Real notifications. A NUMBER — three upcoming events is worth counting. */
  | { kind: 'count'; count: number };

/**
 * Spec rule 2: "Not signed in with a board → save your boards. Signed in → real
 * notifications. Not signed in, no board → nothing. […] It deliberately does not
 * clear until she signs in."
 *
 * The dot surviving capture is the point, not an oversight: journey 5 is the
 * most valuable state in the funnel (she told us who she is and simply has not
 * finished) and it had no signal at all on a phone. So the dot means "there is
 * something unfinished here"; WHAT is unfinished is what the You card says.
 *
 * Note this reverses the 2026-08-13 "the dot means notifications only" call.
 * The two coexist only because signed-out visitors see no alerts at all, so the
 * dot and the count can never both be true.
 */
export function youBadge(s: AccountState): YouBadge {
  if (s.signedIn) {
    return s.alertCount > 0 ? { kind: 'count', count: s.alertCount } : { kind: 'none' };
  }
  if (hasBoard(s)) return { kind: 'dot' };
  return { kind: 'none' };
}

/* ── The You card (mobile) / avatar dropdown (desktop) ────────────────── */

export type YouCardState =
  /** Signed in — notifications, saves, identity. Not an account ask. */
  | { kind: 'signed-in' }
  /** No board yet. No investment to protect, so no ask. */
  | { kind: 'none' }
  /** Has a board, no email yet — the one urgent CTA. */
  | { kind: 'save-boards'; boards: BoardSummary[] }
  /** Has a board, gave us her email, the write landed, has not signed in. */
  | { kind: 'emailed'; email: string; boards: BoardSummary[] }
  /**
   * Has a board and typed an address, but the write was never observed to
   * succeed. A SEPARATE state, not a variant of `emailed`, because the copy
   * must not claim we emailed her — this is the state master silently renders
   * as "You're all set."
   */
  | { kind: 'emailed-unconfirmed'; email: string; boards: BoardSummary[] };

/**
 * ON DESKTOP THE AVATAR *IS* THE YOU TAB (spec rule 4): same content, same
 * states, laid out for a dropdown. So there is one selector, not two — the only
 * difference is that the dropdown's capture ask is a ROW that opens the modal
 * rather than an email field crammed into 240px.
 */
export function youCardState(s: AccountState): YouCardState {
  if (s.signedIn) return { kind: 'signed-in' };
  if (!hasBoard(s)) return { kind: 'none' };
  return { kind: 'save-boards', boards: s.boards };
}

/* ── The gift-guide header slot ───────────────────────────────────────── */

export type GuideAccountSlot =
  | { kind: 'sign-in' }
  /** The demo/shared-board slot hides its account control once signed in. The
   *  actual `/guide/*` route is deliberately richer: V4PublicDoor supplies the
   *  desktop app header or, on mobile, the avatar link to `/you`. */
  | { kind: 'none' };

/**
 * The ONE deliberate exception to "every door renders from the account state":
 * the guide slot renders on a SINGLE AXIS — signed in or not — and ignores
 * capture entirely.
 *
 * Why it must ignore capture: `✓ Sent` on a guide is a receipt for something
 * that happened on a different surface, days ago, about boards this page has
 * never heard of. Journey 5 routes that message to the surface that has the
 * context — the homepage slot when she has no board, the You card when she
 * does, and those two are mutually exclusive by construction.
 *
 * Why `Sign in` must survive signed-out: guides render no SiteHeader and no tab
 * bar, so removing it leaves ZERO doors on the surface Instagram traffic lands
 * on. That is the #291 + #301 lockout shape the reachability invariant exists
 * to prevent (testing.md). The actual public-guide route adds its responsive
 * signed-in navigation in V4PublicDoor; this selector remains the narrow,
 * reusable contract for the demo and shared-board variants.
 */
export function guideAccountSlot(s: AccountState): GuideAccountSlot {
  return s.signedIn ? { kind: 'none' } : { kind: 'sign-in' };
}

/* ── The homepage top-right slot ──────────────────────────────────────── */

export type HomeAccountSlot =
  | { kind: 'avatar' }
  | { kind: 'sign-in' }
  /** Address given, no board: the top right is the only slot she has, so the
   *  receipt lives here. Tapping it opens the explanation card. `confirmed`
   *  false means we never saw the write land — same slot, honest copy. */
  | { kind: 'sent'; email: string; confirmed: boolean }
  /** Has a board, mobile: the bottom tab bar owns account, and a second door
   *  in the header is the duplication `SiteHeader` hides its actions to avoid. */
  | { kind: 'none' };

/**
 * Journey 5: "The two are mutually exclusive — build one per state, never both.
 * No board: the homepage Sign in flips to 'Sent'. Has a board: the You tab card."
 */
export function homeAccountSlot(
  s: AccountState,
  opts: { desktop: boolean },
): HomeAccountSlot {
  if (s.signedIn) return { kind: 'avatar' };
  if (hasBoard(s)) {
    // Desktop has no tab bar, so the avatar IS the You tab and must render
    // (carrying the dot). Mobile defers to the bar.
    return opts.desktop ? { kind: 'avatar' } : { kind: 'none' };
  }
  return { kind: 'sign-in' };
}

/* ── Which sign-in mechanism the door opens ───────────────────────────── */

export type SignInMechanism = { kind: 'full' } | { kind: 'email-password' };

export function signInMechanism(s: AccountState): SignInMechanism {
  return s.browser === 'webview' ? { kind: 'email-password' } : { kind: 'full' };
}

/** Email/password authentication also works inside Meta webviews. */
export function isImpossibleState(_s: AccountState): boolean {
  return false;
}
